import { toast } from 'react-toastify';
import { STRING_CONSTANTS } from '../constants/common';

// Track last error and success messages with timestamps to prevent duplicates
let lastError = { message: null, timestamp: 0 };
let lastSuccess = { message: null, timestamp: 0 };
const DUPLICATE_THRESHOLD = 2000; // 2 seconds

// Notify error function
export const notifyError = (message) => {
  if (message) {
    const now = Date.now();
    // Prevent duplicate error messages within threshold
    if (lastError.message === message && (now - lastError.timestamp) < DUPLICATE_THRESHOLD) {
      return;
    }
    lastError = { message, timestamp: now };
    toast.error(message, {
      toastId: `error-${message}`, // Use unique ID to prevent duplicates
      autoClose: 3000,
    });
  }
};

// Notify success function
export const notifySuccess = (message) => {
  if (message) {
    const now = Date.now();
    // Prevent duplicate success messages within threshold
    if (lastSuccess.message === message && (now - lastSuccess.timestamp) < DUPLICATE_THRESHOLD) {
      return;
    }
    lastSuccess = { message, timestamp: now };
    toast.success(message, {
      toastId: `success-${message}`, // Use unique ID to prevent duplicates
      autoClose: 2000,
    });
  }
};

// Storage utility functions (without encryption for now)
// If encryption is needed, install crypto-js: npm install crypto-js

export const getStorageItem = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    }
    return null;
  } catch (error) {
    return localStorage.getItem(key);
  }
};

export const setStorageItem = (key, value) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  } catch (error) {
    console.error('Error saving to storage:', error);
  }
};

export const clearLocalStorage = (excludeKeys = []) => {
  if (
    Array.isArray(excludeKeys) &&
    !excludeKeys?.includes(STRING_CONSTANTS.STORED_KEYS.LOGIN_CREDENTIALS)
  ) {
    excludeKeys.push(STRING_CONSTANTS.STORED_KEYS.LOGIN_CREDENTIALS);
  }

  const preservedData = {};

  if (excludeKeys.length > 0) {
    excludeKeys?.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preservedData[key] = value;
      }
    });
  }

  localStorage.clear();

  Object.keys(preservedData).forEach((key) => {
    localStorage.setItem(key, preservedData[key]);
  });
};

// Utility functions for useForm hook
export const get_prop_values = (obj, prop = null) => {
  if (!obj || typeof obj !== 'object') return {};
  const result = {};
  Object.keys(obj).forEach((key) => {
    if (prop) {
      result[key] = obj[key]?.[prop] ?? '';
    } else {
      result[key] = false;
    }
  });
  return result;
};

export const is_required = (value, required, errorMessage) => {
  if (required && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
    return errorMessage || 'This field is required';
  }
  return '';
};

export const is_object = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Check if API response indicates RBAC permission denied (backend returns status: false with permission message).
 * @param {Object} response - axios response (response.data) or error.response
 * @returns {boolean}
 */
export const isPermissionDeniedResponse = (response) => {
  if (!response) return false;
  const data = response?.data ?? response;
  const status = data?.status;
  const message = (data?.message || data?.error || '').toString().toLowerCase();
  if (status === false || status === 'false') {
    return (
      message.includes('permission') ||
      message.includes('do not have permission') ||
      message === 'you do not have permission for this action'
    );
  }
  return false;
};

/**
 * Extract error message from API error object or standard Error
 * @param {Object|Error} error - Error object from catch block
 * @param {string} fallback - Fallback message if no message found
 * @returns {string} The extracted error message
 */
export const getErrorMessage = (error, fallback = STRING_CONSTANTS.MESSAGES.ERROR.GENERIC) => {
  if (!error) return fallback;
  
  // Extract message from various possible error object structures
  // 1. axios error.response.data
  // 2. intercepted error (which is error.response)
  // 3. standard Error object
  const data = error?.response?.data || error?.data || error;
  
  return data?.message || data?.error || error?.message || fallback;
};

export const convertBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Confirmation utility function
// Wraps window.confirm to provide consistent confirmation dialogs
// Can be replaced with a custom modal component in the future
export const confirmAction = (message) => {
  // eslint-disable-next-line no-alert
  return window.confirm(message);
};

export const formatRelativeTime = (timestamp, t) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('just_now');
  if (diffMins < 60) return `${diffMins}${t('m_ago')}`;
  if (diffHours < 24) return `${diffHours}${t('h_ago')}`;
  return `${diffDays}${t('d_ago')}`;
};

export const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && isNaN(Date.parse(timestamp))) {
    return timestamp;
  }
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleString();
};

export const calculateResolvingTime = (createdAt, t) => {
  if (!createdAt) return '-';

  const start = new Date(createdAt);
  if (isNaN(start.getTime())) return '-';

  const diffMs = new Date() - start;
  const diffDays = Math.floor(diffMs / 86400000);
  const diffHours = Math.floor((diffMs % 86400000) / 3600000);
  const diffMins = Math.floor((diffMs % 3600000) / 60000);

  const parts = [];
  if (diffDays > 0) parts.push(`${diffDays}${t('d') || 'd'}`);
  if (diffHours > 0) parts.push(`${diffHours}${t('h') || 'h'}`);
  if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}${t('m') || 'm'}`);

  return parts.join(' ');
};

// Export utility functions
/**
 * Export data to CSV format
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with key and label
 * @param {String} filename - Name of the file (without extension)
 */
export const exportToCSV = (data, columns, filename = 'export') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      notifyError('No data to export');
      return;
    }

    // Get headers from columns
    const headers = columns.map(col => col.label || col.key);
    
    // Get data rows
    const rows = data.map(item => {
      return columns.map(col => {
        let value;
        // Use render function if available, otherwise get value directly
        if (col.render && typeof col.render === 'function') {
          value = col.render(item[col.key], item);
        } else {
          value = item[col.key];
        }
        // Handle null/undefined values
        if (value === null || value === undefined) return '';
        // Handle objects and arrays
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Add BOM for UTF-8 to support Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notifySuccess('CSV exported successfully');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    notifyError('Failed to export CSV');
  }
};

/**
 * Export data to Excel format (CSV with .xlsx extension)
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with key and label
 * @param {String} filename - Name of the file (without extension)
 */
export const exportToExcel = (data, columns, filename = 'export') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      notifyError('No data to export');
      return;
    }

    // Get headers from columns
    const headers = columns.map(col => col.label || col.key);
    
    // Get data rows
    const rows = data.map(item => {
      return columns.map(col => {
        let value;
        // Use render function if available, otherwise get value directly
        if (col.render && typeof col.render === 'function') {
          value = col.render(item[col.key], item);
        } else {
          value = item[col.key];
        }
        // Handle null/undefined values
        if (value === null || value === undefined) return '';
        // Handle objects and arrays
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Add BOM for UTF-8 to support Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notifySuccess('Excel file exported successfully');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    notifyError('Failed to export Excel file');
  }
};

/**
 * Export data to PDF format (using HTML table and print)
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column definitions with key and label
 * @param {String} filename - Name of the file (without extension)
 * @param {String} title - Title for the PDF document
 */
export const exportToPDF = (data, columns, filename = 'export', title = 'Export') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      notifyError('No data to export');
      return;
    }

    // Get headers from columns
    const headers = columns.map(col => col.label || col.key);
    
    // Build HTML table
    let tableRows = '';
    data.forEach(item => {
      const cells = columns.map(col => {
        let value;
        // Use render function if available, otherwise get value directly
        if (col.render && typeof col.render === 'function') {
          value = col.render(item[col.key], item);
        } else {
          value = item[col.key];
        }
        const displayValue = value === null || value === undefined ? '' : String(value);
        return `<td>${displayValue}</td>`;
      }).join('');
      tableRows += `<tr>${cells}</tr>`;
    });

    const headerCells = headers.map(header => `<th>${header}</th>`).join('');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              body {
                margin: 0;
              }
              @page {
                margin: 1cm;
              }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>${headerCells}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Also open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    
    notifySuccess('PDF export initiated. Use browser print to save as PDF.');
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    notifyError('Failed to export PDF');
  }
};
